import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, InstagramLogo, FacebookLogo, ArrowRight } from 'phosphor-react';
import { useQuery } from '@tanstack/react-query';
import memberService, { BoxPro } from '@/services/member.service';
import './MeetYourPro.css';

const ProModal = ({ pro, onClose }: { pro: BoxPro; onClose: () => void }) => {
  const fullName = `${pro.firstName} ${pro.lastName}`;
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div className="myp-fullscreen">
      {/* Top bar */}
      <div className="myp-fs-topbar">
        <div style={{ width: 40 }} />
        <span className="myp-fs-topbar-title">Box Pro</span>
        <button className="myp-fs-close" onClick={onClose} aria-label="Close">
          <X size={20} weight="bold" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="myp-fs-body">
        {/* Hero identity */}
        <div className="myp-fs-hero">
          {pro.profilePhoto && !imgFailed ? (
            <img src={pro.profilePhoto} alt={fullName} className="myp-fs-photo" onError={() => setImgFailed(true)} />
          ) : (
            <div className="myp-fs-photo myp-fs-photo-placeholder">
              {pro.firstName[0]}{pro.lastName[0]}
            </div>
          )}
          <p className="myp-fs-name">{fullName}</p>
          <p className="myp-fs-role">Box Pro</p>
        </div>

        {/* Sections */}
        <div className="myp-fs-sections">
          {pro.bio && (
            <div className="myp-modal-section">
              <p className="myp-modal-section-label">About</p>
              <p className="myp-modal-section-text">{pro.bio}</p>
            </div>
          )}
          {pro.experience && (
            <div className="myp-modal-section">
              <p className="myp-modal-section-label">Experience</p>
              <p className="myp-modal-section-text">{pro.experience}</p>
            </div>
          )}
          {pro.certifications && (
            <div className="myp-modal-section">
              <p className="myp-modal-section-label">Certifications</p>
              <p className="myp-modal-section-text">{pro.certifications}</p>
            </div>
          )}

          {/* Social links */}
          {(pro.facebookUrl || pro.instagramUrl) && (
            <div className="myp-modal-socials">
              {pro.facebookUrl && (
                <a href={pro.facebookUrl} target="_blank" rel="noopener noreferrer" className="myp-social-link">
                  <FacebookLogo size={20} weight="fill" />
                  <span>Facebook</span>
                </a>
              )}
              {pro.instagramUrl && (
                <a href={pro.instagramUrl} target="_blank" rel="noopener noreferrer" className="myp-social-link">
                  <InstagramLogo size={20} weight="fill" />
                  <span>Instagram</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const ProCard = ({ pro, onReadIntro }: { pro: BoxPro; onReadIntro: () => void }) => {
  const fullName = `${pro.firstName} ${pro.lastName}`;
  const hasBio = !!pro.bio;
  const [imgFailed, setImgFailed] = useState(false); // eslint-disable-line

  return (
    <div className="myp-card">
      {/* Identity */}
      <div className="myp-identity">
        <div className="myp-photo-wrap">
          {pro.profilePhoto && !imgFailed ? (
            <img src={pro.profilePhoto} alt={fullName} className="myp-photo" onError={() => setImgFailed(true)} />
          ) : (
            <div className="myp-photo-placeholder myp-photo-initials">
              {pro.firstName[0]}{pro.lastName[0]}
            </div>
          )}
        </div>

        <div className="myp-identity-info">
          <span className="myp-name">{fullName}</span>
          <span className="myp-subtitle">Your Box Pro</span>
        </div>
      </div>

      {/* Bio — clamped, opens modal on Show more */}
      {hasBio && (
        <div className="myp-quote-block">
          <p className="myp-quote-text myp-quote-text--clamped">
            "{pro.bio}"
          </p>
          <button className="myp-show-toggle" onClick={onReadIntro}>
            Show more <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
};

const MeetYourPro = () => {
  const [selectedPro, setSelectedPro] = useState<BoxPro | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const { data: pros = [], isLoading } = useQuery<BoxPro[]>({
    queryKey: ['boxPros'],
    queryFn: () => memberService.getBoxPros(),
    staleTime: 5 * 60 * 1000,
  });

  // Sync scroll position → dot indicator
  useEffect(() => {
    const track = trackRef.current;
    if (!track || pros.length <= 1) return;
    const onScroll = () => {
      const index = Math.round(track.scrollLeft / track.offsetWidth);
      setActiveIndex(index);
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [pros.length]);

  if (isLoading || pros.length === 0) return null;

  return (
    <div className="myp-section">
      <div className="myp-section-header">
        <span className="myp-section-title">Your Pro</span>
      </div>

      {/* Carousel track */}
      <div className="myp-track" ref={trackRef}>
        {pros.map((pro) => (
          <div key={pro.id} className="myp-slide">
            <ProCard pro={pro} onReadIntro={() => setSelectedPro(pro)} />
          </div>
        ))}
      </div>

      {/* Dot indicators — only when multiple */}
      {pros.length > 1 && (
        <div className="myp-dots">
          {pros.map((_, i) => (
            <button
              key={i}
              className={`myp-dot${i === activeIndex ? ' myp-dot--active' : ''}`}
              onClick={() => {
                trackRef.current?.scrollTo({ left: i * trackRef.current.offsetWidth, behavior: 'smooth' });
              }}
              aria-label={`Pro ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Full intro modal */}
      {selectedPro && (
        <ProModal pro={selectedPro} onClose={() => setSelectedPro(null)} />
      )}
    </div>
  );
};

export default MeetYourPro;
